import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { useRULBuckets } from "../utils/rulBuckets";
import RULScatterChart from "../../../../Charts/Scatter/RULScatterChart";
import { countUnavailableBatteries } from "../../../../Charts/Scatter/scatterBucketUtils";

const RUL_DETAILS_SCATTER_ID = "rul-details-scatter-chart";

/**
 * RUL details scatter chart.
 * Thin wrapper around RULScatterChart — groups pre-classified batteries by
 * category and delegates all rendering to the shared component.
 *
 * Props
 *   batteries          — pre-classified battery objects (have .rulCategory set)
 *   selectedCategories — Set<string>
 *   onCategorySelect   — callback(name) to toggle a category
 *   activeYFilter      — "months" | "cycles" | "km"
 */
const RULDetailsChart = ({
  batteries = [],
  selectedCategories,
  onCategorySelect,
  activeYFilter,
  supportedUnits,
  onDotClick,
}) => {
  const rulBuckets        = useRULBuckets();
  const activeUnitBuckets = useMemo(() => rulBuckets[activeYFilter] ?? [], [rulBuckets, activeYFilter]);

  const batteriesByVisibleCategory = useMemo(
    () =>
      activeUnitBuckets
        .filter((cat) => selectedCategories.size === 0 || selectedCategories.has(cat.name))
        .map((cat) => ({
          category:  cat,
          batteries: batteries.filter((b) => b.rulCategory === cat.name),
        })),
    [batteries, selectedCategories, activeUnitBuckets]
  );

  // Legend count for "RUL Unavailable" (with or without a manufacturing date),
  // independent of the current selection.
  const unavailableCount = useMemo(
    () => countUnavailableBatteries(batteries, activeUnitBuckets, "rulCategory"),
    [batteries, activeUnitBuckets]
  );

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
      <Box sx={{ overflowX: "auto", overflowY: "hidden", width: "100%", maxWidth: "100%", minWidth: 0 }}>
        <Box sx={{ width: "max(100%, 700px)", minWidth: 0 }}>
          <RULScatterChart
            batteriesByVisibleCategory={batteriesByVisibleCategory}
            selectedCategories={selectedCategories}
            onCategorySelect={onCategorySelect}
            activeYFilter={activeYFilter}
            supportedUnits={supportedUnits}
            onDotClick={onDotClick}
            unavailableCount={unavailableCount}
            chartId={RUL_DETAILS_SCATTER_ID}
            height={360}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default RULDetailsChart;
